/**
 * ThemeProvider — light/dark/system theme management.
 *
 * Sprint AO. Drives a single `data-theme` attribute on the
 * `document.documentElement`; `@vseh/tokens/tokens.css` already
 * defines a `[data-theme="dark"]` cascade so the entire token set
 * (background, text, border, shadows, focus rings) switches at once.
 *
 * Three preferences:
 *   - "light"  — force light, ignore OS (default)
 *   - "dark"   — force dark, ignore OS
 *   - "system" — follow `prefers-color-scheme`
 *
 * Persistence: `localStorage["vseh.theme"]`. Reads on mount, writes
 * on every change. No-op for SSR contexts (the admin app is Vite SPA;
 * a guard kept for future TWA reuse).
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from "react";


export type ThemeChoice = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";


interface ThemeContextValue {
  choice:   ThemeChoice;      // what the user picked
  resolved: ResolvedTheme;    // what's actually applied right now
  setChoice: (next: ThemeChoice) => void;
}


const ThemeContext = createContext<ThemeContextValue | null>(null);
const STORAGE_KEY  = "vseh.theme";


function readSavedChoice(): ThemeChoice {
  // Default is "light" rather than "system" — a Russian
  // operator on a macOS laptop with auto-night-shift on
  // shouldn't get a dark UI for a tool they expect to be
  // bright. The header toggle still offers all three
  // options for users who want OS-following or forced dark.
  if (typeof localStorage === "undefined") return "light";
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw === "light" || raw === "dark" || raw === "system"
    ? raw
    : "light";
}


function systemPrefersDark(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}


function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === "undefined") return;
  // Setting the attribute (not a class) matches the token CSS contract
  // and keeps the cascade specificity predictable.
  document.documentElement.dataset.theme = resolved;
}


export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [choice, setChoiceState] = useState<ThemeChoice>(readSavedChoice);
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);

  // Listen for OS-level changes so "system" stays in sync without
  // a page reload (matters when users use macOS auto-night-shift).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const resolved: ResolvedTheme = useMemo(
    () => choice === "system" ? (systemDark ? "dark" : "light") : choice,
    [choice, systemDark],
  );

  // Apply on every resolved change. Cheap (one attribute write).
  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Quota / privacy mode — preference still works for this session.
    }
  }, []);

  const value = useMemo(
    () => ({ choice, resolved, setChoice }),
    [choice, resolved, setChoice],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}


export function useTheme(): ThemeContextValue {
  const v = useContext(ThemeContext);
  if (v === null) {
    throw new Error("useTheme() must be used inside <ThemeProvider>");
  }
  return v;
}
